import React, { useState, useEffect } from 'react';

/**
 * AdminGate — Server-side authenticated password gate for admin pages.
 * 
 * Validates password against the Express server (POST /api/admin/auth),
 * which returns a session token. The token is stored in sessionStorage
 * and passed to children via the onToken callback pattern.
 * 
 * The password NEVER exists in the client bundle — it lives only
 * in the server's environment variables.
 */
const SESSION_KEY = 'tcc_admin_token';

export default function AdminGate({ children }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Check if existing token is still valid on mount
  useEffect(() => {
    const storedToken = sessionStorage.getItem(SESSION_KEY);
    if (!storedToken) {
      setLoading(false);
      return;
    }

    fetch('/api/admin/verify', {
      headers: { 'Authorization': `Bearer ${storedToken}` },
    })
      .then(res => {
        if (res.ok) {
          setAuthenticated(true);
        } else {
          sessionStorage.removeItem(SESSION_KEY);
        }
      })
      .catch(() => {
        sessionStorage.removeItem(SESSION_KEY);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch('/api/admin/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await res.json();

      if (res.ok && data.success && data.token) {
        sessionStorage.setItem(SESSION_KEY, data.token);
        setAuthenticated(true);
        setPassword('');
      } else {
        setError(data.error || 'Authentication failed');
        setPassword('');
        setTimeout(() => setError(''), 3000);
      }
    } catch (err) {
      setError('Server unreachable — try again');
      setTimeout(() => setError(''), 3000);
    }

    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="w-5 h-5 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (authenticated) {
    return children;
  }

  return (
    <section className="flex items-center justify-center min-h-[70vh] px-6 py-20">
      <div className="text-center max-w-sm mx-auto w-full">
        {/* Lock icon */}
        <div className="relative inline-flex items-center justify-center mb-8">
          <div
            className="absolute -inset-8 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(245,158,11,0.08), transparent 70%)',
              filter: 'blur(16px)',
            }}
          />
          <div
            className="relative w-20 h-20 rounded-full flex items-center justify-center"
            style={{
              background: 'linear-gradient(145deg, rgba(245,158,11,0.08), rgba(245,158,11,0.02))',
              border: '1.5px solid rgba(245,158,11,0.15)',
              boxShadow: '0 0 40px rgba(245,158,11,0.06)',
            }}
          >
            <svg className="w-8 h-8 text-amber-400/60" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
            </svg>
          </div>
        </div>

        <h1 className="text-lg font-bold text-white/80 mb-2">
          Staff Only
        </h1>
        <p className="text-xs text-white/35 font-mono mb-8">
          This area is restricted to Country Club staff
        </p>

        {/* Password form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter admin password"
              autoFocus
              disabled={submitting}
              className="w-full px-4 py-3 rounded-lg text-sm font-mono text-center bg-white/[0.04] border border-white/[0.1] text-white/90 placeholder-white/25 focus:outline-none focus:border-amber-500/40 transition-colors disabled:opacity-50"
              style={{
                boxShadow: error
                  ? '0 0 0 1px rgba(239,68,68,0.3), 0 0 20px rgba(239,68,68,0.05)'
                  : 'none',
              }}
            />
          </div>

          {error && (
            <p className="text-xs font-mono text-red-400/70 animate-pulse">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full px-5 py-2.5 rounded-lg text-sm font-mono font-semibold transition-all duration-300 hover:scale-[1.01] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: 'linear-gradient(135deg, rgba(245,158,11,0.12), rgba(245,158,11,0.05))',
              border: '1px solid rgba(245,158,11,0.2)',
              color: 'rgba(245,158,11,0.85)',
              boxShadow: '0 4px 16px rgba(245,158,11,0.08)',
            }}
          >
            {submitting ? 'Authenticating...' : 'Unlock'}
          </button>
        </form>

        <p className="mt-10 text-[10px] font-mono text-white/12 tracking-wider uppercase">
          Members only · Badge required
        </p>
      </div>
    </section>
  );
}

/**
 * Helper to get the admin token for use in fetch() calls.
 * Import this in AdminEvents to attach to API requests.
 */
export function getAdminToken() {
  return sessionStorage.getItem('tcc_admin_token');
}
